import { useState } from 'react'
import { AlertOctagon, CalendarClock, FileCheck2, Files, Search, X } from 'lucide-react'
import { EmptyState, ErrorState, KpiGrid, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { cn } from '@/lib/utils'
import { DocumentStatusBadge } from '../components/DocumentStatusBadge'
import { useExpiringCompanyDocuments } from '../api'
import type { DocumentStatus } from '../types'

const FILTERS = [
  { key: 'ATTENTION', label: 'Needs attention' },
  { key: 'EXPIRED', label: 'Expired' },
  { key: 'EXPIRING', label: 'Expiring' },
  { key: 'ALL', label: 'Everything' },
] as const

type Filter = (typeof FILTERS)[number]['key']

function matches(status: DocumentStatus, filter: Filter): boolean {
  if (filter === 'ALL') return true
  if (filter === 'ATTENTION') return status === 'EXPIRED' || status === 'EXPIRING'
  return status === filter
}

/**
 * Everything running out, across every entity the signed-in user can see.
 *
 * <p>Defaults to "needs attention" rather than everything: a list of two hundred valid
 * documents with four expired ones buried in it is a list that hides its own point.
 */
export function ExpiringDocumentsPage() {
  useBreadcrumb()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const entityNames = new Map((entities.data ?? []).map((entity) => [entity.id, entity.name]))
  const documents = useExpiringCompanyDocuments([...entityNames.keys()])

  const [filter, setFilter] = useState<Filter>('ATTENTION')
  const [search, setSearch] = useState('')

  const counts: Record<Filter, number> = {
    ATTENTION: documents.data.filter((document) => matches(document.status, 'ATTENTION')).length,
    EXPIRED: documents.data.filter((document) => document.status === 'EXPIRED').length,
    EXPIRING: documents.data.filter((document) => document.status === 'EXPIRING').length,
    ALL: documents.data.length,
  }

  const needle = search.trim().toLowerCase()
  const rows = documents.data
    .filter((document) => matches(document.status, filter))
    .filter((document) => !needle || [
      document.documentTypeName,
      document.documentNumber,
      entityNames.get(document.legalEntityId),
    ].some((value) => value?.toLowerCase().includes(needle)))
    .sort((left, right) =>
      (left.daysUntilExpiry ?? Number.MAX_SAFE_INTEGER) - (right.daysUntilExpiry ?? Number.MAX_SAFE_INTEGER))

  return (
    <div className="space-y-5">
      <PageHeader
        title="Expiring documents"
        subtitle="Company licences and cards, soonest first. People's own documents are on their records."
      />

      {documents.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-[104px] rounded-[var(--radius-card)]" />)}
        </div>
      ) : (
        <KpiGrid
          items={[
            { label: 'Expired', value: counts.EXPIRED, icon: AlertOctagon, tint: counts.EXPIRED > 0 ? 'red' : 'green' },
            { label: 'Expiring soon', value: counts.EXPIRING, icon: CalendarClock, tint: counts.EXPIRING > 0 ? 'orange' : 'green' },
            { label: 'Valid', value: documents.data.filter((document) => document.status === 'VALID').length, icon: FileCheck2, tint: 'green' },
            { label: 'Company documents', value: counts.ALL, icon: Files, tint: 'purple' },
          ]}
        />
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] p-4">
          <div
            className="inline-flex flex-wrap gap-1 rounded-[var(--radius-control)] bg-[var(--color-page)] p-1"
            role="group"
            aria-label="Filter by status"
          >
            {FILTERS.map((option) => {
              const active = filter === option.key
              return (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter(option.key)}
                  className={cn(
                    'inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-medium transition-colors',
                    active
                      ? 'bg-[var(--color-card)] text-[var(--color-heading)] shadow-sm'
                      : 'text-[var(--color-muted)] hover:text-[var(--color-heading)]',
                  )}
                >
                  {option.label}
                  {!documents.isPending && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'rounded-full px-1.5 text-[11px] tabular-nums',
                        active ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]' : 'bg-[var(--color-card)]',
                      )}
                    >
                      {counts[option.key]}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]" aria-hidden="true" />
            <Input
              aria-label="Search documents"
              placeholder="Search type, number or entity"
              className="ps-9 pe-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                className="absolute end-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-[var(--color-muted)] hover:text-[var(--color-heading)]"
                onClick={() => setSearch('')}
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {documents.isPending && (
          <div className="space-y-3 p-4">
            {[0, 1, 2].map((index) => <Skeleton key={index} className="h-10" />)}
          </div>
        )}
        {documents.isError && <ErrorState error={new Error('Documents could not be loaded')} />}
        {!documents.isPending && rows.length === 0 && (
          <EmptyState
            icon={needle ? Search : FileCheck2}
            title={needle ? 'No document matches that search' : 'Nothing to act on'}
            description={needle
              ? 'Try a document type, a number or an entity name.'
              : 'No company document in scope matches that filter.'}
            action={needle
              ? <Button variant="outline" size="sm" onClick={() => setSearch('')}>Clear search</Button>
              : undefined}
          />
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr>
                  <th scope="col" className="px-4 py-2.5 text-start">Document</th>
                  <th scope="col" className="px-4 py-2.5 text-start">Entity</th>
                  <th scope="col" className="px-4 py-2.5 text-start">Number</th>
                  <th scope="col" className="px-4 py-2.5 text-start">Expires</th>
                  <th scope="col" className="px-4 py-2.5 text-start">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((document) => (
                  <tr key={document.id} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-3 font-medium text-[var(--color-heading)]">
                      {document.documentTypeName}
                    </td>
                    <td className="px-4 py-3 text-[var(--color-muted)]">
                      {entityNames.get(document.legalEntityId) ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      {document.documentNumber
                        ? <code className="rounded bg-[var(--color-page)] px-1.5 py-0.5 text-xs">{document.documentNumber}</code>
                        : <span className="text-[var(--color-muted)]">—</span>}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{document.expiryDate ?? '—'}</td>
                    <td className="px-4 py-3">
                      <DocumentStatusBadge
                        status={document.status}
                        daysUntilExpiry={document.daysUntilExpiry}
                        withPhrase
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
